import Alert from "@/components/Alert";
import { Button } from "@/components/Button";
import { Spinner } from "@/components/Spinner";
import { EXTERNAL_API_URL } from "@/utils/constants";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router";
import {
  buildSurveyResponsePayload,
  isQuestionAnswered,
  sortQuestions,
  SurveyAnswer,
  SurveyDetails,
  SurveyQuestionCard,
  SurveyResponsePayload,
  Track,
} from "../survey";
import { SurveyExternalContext } from "./context";

type ExternalSurveyResponse = SurveyDetails | { Data?: SurveyDetails };

function getExternalAuthHeaders(token: string) {
  return {
    Authorization: `livly-token ${token}`,
    "Content-Type": "application/json",
  };
}

function normalizeSurveyResponse(response: ExternalSurveyResponse): SurveyDetails {
  if ("Data" in response && response.Data) {
    return response.Data;
  }

  return response as SurveyDetails;
}

async function getExternalSurvey(token: string) {
  const result = await axios.get<ExternalSurveyResponse>(
    `${EXTERNAL_API_URL}/survey?ltoken=${token}`,
    {
      headers: getExternalAuthHeaders(token),
    }
  );

  return normalizeSurveyResponse(result.data);
}

async function getExternalSurveyByDelivery(
  token: string,
  surveyDeliveryId: number
) {
  const result = await axios.get<ExternalSurveyResponse>(
    `${EXTERNAL_API_URL}/survey/delivery/${surveyDeliveryId}?ltoken=${token}`,
    {
      headers: getExternalAuthHeaders(token),
    }
  );

  return normalizeSurveyResponse(result.data);
}

async function getExternalSurveyWithFallback(
  token: string,
  surveyDeliveryId: number
) {
  try {
    return await getExternalSurvey(token);
  } catch {
    return await getExternalSurveyByDelivery(token, surveyDeliveryId);
  }
}

async function postExternalSurvey(token: string, payload: SurveyResponsePayload) {
  await axios.post(`${EXTERNAL_API_URL}/survey?ltoken=${token}`, payload, {
    headers: getExternalAuthHeaders(token),
  });
}

async function postExternalSurveyByDelivery(
  token: string,
  surveyDeliveryId: number,
  payload: SurveyResponsePayload
) {
  await axios.post(
    `${EXTERNAL_API_URL}/survey/delivery/${surveyDeliveryId}/response?ltoken=${token}`,
    payload,
    {
      headers: getExternalAuthHeaders(token),
    }
  );
}

async function postExternalSurveyWithFallback(
  token: string,
  surveyDeliveryId: number,
  payload: SurveyResponsePayload
) {
  try {
    await postExternalSurvey(token, payload);
  } catch {
    await postExternalSurveyByDelivery(token, surveyDeliveryId, payload);
  }
}

export default function SurveyExternalSubmitPage() {
  const navigate = useNavigate();
  const { token, surveyDeliveryId } = useOutletContext<SurveyExternalContext>();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, SurveyAnswer>>({});

  const { data, isLoading, isError } = useQuery({
    queryKey: ["survey", "external", "details", surveyDeliveryId],
    queryFn: () => getExternalSurveyWithFallback(token, surveyDeliveryId),
  });

  const questions = useMemo(() => sortQuestions(data?.questions), [data?.questions]);
  const total = questions.length;
  const question = questions[step];
  const questionId = question?.surveyQuestionId ?? 0;
  const answer = answers[questionId];
  const isLastQuestion = step === total - 1;
  const isCurrentQuestionValid = isQuestionAnswered(question, answer);

  const submitResponseMutation = useMutation({
    mutationFn: (payload: SurveyResponsePayload) =>
      postExternalSurveyWithFallback(token, surveyDeliveryId, payload),
    onSuccess: () => {
      navigate("/survey/thank-you");
    },
  });

  const onSetAnswer = (next: SurveyAnswer) => {
    if (!questionId) {
      return;
    }

    setAnswers((current) => ({
      ...current,
      [questionId]: next,
    }));
  };

  const onSubmit = () => {
    if (submitResponseMutation.isLoading) {
      return;
    }

    const payload = buildSurveyResponsePayload(questions, answers);
    submitResponseMutation.mutate(payload);
  };

  if (isLoading) {
    return (
      <div className="h-screen flex justify-center items-center">
        <Spinner color="livly" size="xl" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="max-w-2xl p-6 mx-auto">
        <Alert message="There was an error loading survey. Please try again." />
      </div>
    );
  }

  if (!question) {
    return (
      <div className="max-w-2xl p-6 mx-auto">
        <Alert variant="default" message="This survey does not have any questions yet." />
      </div>
    );
  }

  if (data.hasResponded) {
    return (
      <div className="flex flex-col items-center max-w-lg p-4 mx-auto text-center">
        <h2 className="text-lg font-bold">{data.title ?? "Survey"}</h2>
        <p className="mt-2">Already answered.</p>
        <img src="/thumbs_up.png" className="h-48 py-8" />
        <Link to="/" className="w-full">
          <Button color="secondary" className="w-full">
            Done
          </Button>
        </Link>
        <div className="w-full mt-4">
          <Alert
            variant="default"
            message="You have already submitted this survey response."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl p-4 pb-36 mx-auto">
      <h1 className="text-2xl font-semibold text-gray-900">{data.title ?? "Survey"}</h1>
      {total > 1 && <Track step={step} total={total} />}

      <div className="mt-6 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-5 py-4">
          <div className="mt-2 flex justify-between gap-3 text-sm text-gray-500">
            <span>{`${total} question${total === 1 ? "" : "s"}`}</span>
          </div>
        </div>

        <div className="px-5 py-6">
          {submitResponseMutation.isError && (
            <div className="mb-4">
              <Alert message="Unable to submit survey right now. Please try again." />
            </div>
          )}
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">
            Question {step + 1}/{total}
          </p>
          <div className="mt-3 flex items-start gap-2">
            <h2 className="text-lg font-normal leading-tight text-gray-900">
              {question.questionText}
            </h2>
            {question.isRequired ? (
              <span className="mt-1 text-sm font-medium text-[#F9877E]">*</span>
            ) : null}
          </div>

          <div className="mt-6">
            <SurveyQuestionCard
              question={question}
              answer={answer}
              onAnswerChange={onSetAnswer}
            />
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 flex items-center justify-end m-4 bg-white border border-gray-100 rounded-lg shadow-lg">
        <div className="flex flex-col items-center justify-end w-full gap-3 p-4 border-t border-gray-200 md:flex-row md:border-none">
          {step !== 0 && (
            <Button
              color="primary"
              outlined
              onClick={() => setStep((current) => current - 1)}
              className="w-full md:w-auto"
            >
              Previous
            </Button>
          )}

          {isLastQuestion ? (
            <Button
              color="secondary"
              disabled={!isCurrentQuestionValid || submitResponseMutation.isLoading}
              onClick={onSubmit}
              className="w-full md:w-auto"
            >
              {submitResponseMutation.isLoading ? "Submitting..." : "Submit"}
            </Button>
          ) : (
            <Button
              color="secondary"
              onClick={() => setStep((current) => current + 1)}
              disabled={!isCurrentQuestionValid}
              className="w-full md:w-auto"
            >
              Next
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
